import { Controller } from "@hotwired/stimulus"

// Composite picker: pg_search-backed type-ahead + "+ Dodaj kontrahenta"
// modal that POSTs JSON to /vendors and drops the new record straight
// into the picker without a page reload.
//
// Markup contract:
//   data-controller="vendor-picker"
//   data-vendor-picker-search-url-value=".../vendors/search"
//   data-vendor-picker-create-url-value=".../vendors.json"
//
// Targets: input, hidden, results, modal, modalForm, modalErrors
export default class extends Controller {
  static targets = ["input", "hidden", "results", "modal", "modalForm", "modalErrors"]
  static values  = { searchUrl: String, createUrl: String }

  connect() {
    this._timeout = null
    this._onClickOutside = this._onClickOutside.bind(this)
    document.addEventListener("click", this._onClickOutside)
  }

  disconnect() {
    clearTimeout(this._timeout)
    document.removeEventListener("click", this._onClickOutside)
  }

  // ── Autocomplete ──────────────────────────────────────────────────────

  search() {
    clearTimeout(this._timeout)
    this.hiddenTarget.value = ""

    const q = this.inputTarget.value.trim()
    if (q.length < 2) {
      this._hideResults()
      return
    }
    this._timeout = setTimeout(() => this._fetchResults(q), 200)
  }

  clear() {
    this.inputTarget.value = ""
    this.hiddenTarget.value = ""
    this._hideResults()
  }

  select(event) {
    const div = event.currentTarget
    this.inputTarget.value = div.dataset.recordName
    this.hiddenTarget.value = div.dataset.recordId
    this._hideResults()
  }

  // ── Modal create ──────────────────────────────────────────────────────

  openModal(event) {
    event?.preventDefault()
    // Pre-fill the modal name field with whatever's in the autocomplete input —
    // saves a step if the user typed something that returned no results.
    const nameField = this.modalFormTarget.querySelector('[name="vendor[name]"]')
    if (nameField && !nameField.value) nameField.value = this.inputTarget.value.trim()
    this._renderErrors([])
    this.modalTarget.showModal()
  }

  closeModal(event) {
    event?.preventDefault()
    this.modalTarget.close()
  }

  // Backdrop click — close the modal when the user clicks outside the form.
  modalBackdropClick(event) {
    if (event.target === this.modalTarget) this.modalTarget.close()
  }

  async submitCreate(event) {
    event.preventDefault()
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content

    // The modal "form" is a <div>, not a <form>, because nested <form> tags
    // are invalid HTML5. Collect the named inputs manually.
    const formData = new FormData()
    this.modalFormTarget.querySelectorAll("input[name], select[name], textarea[name]").forEach(el => {
      if (el.name) formData.append(el.name, el.value)
    })

    const res = await fetch(this.createUrlValue, {
      method:  "POST",
      headers: { "Accept": "application/json", "X-CSRF-Token": csrf || "" },
      body:    formData
    })

    if (res.ok) {
      const record = await res.json()
      this.inputTarget.value  = record.name
      this.hiddenTarget.value = record.id
      this._resetModalInputs()
      this._renderErrors([])
      this.modalTarget.close()
    } else {
      let payload = {}
      try { payload = await res.json() } catch (_) { /* non-JSON error body */ }
      this._renderErrors(payload.errors || ["Nie udało się utworzyć kontrahenta."])
    }
  }

  _resetModalInputs() {
    this.modalFormTarget.querySelectorAll("input[name]").forEach(el => { el.value = "" })
  }

  // ── Private ───────────────────────────────────────────────────────────

  async _fetchResults(query) {
    const url = new URL(this.searchUrlValue, window.location.origin)
    url.searchParams.set("q", query)
    try {
      const res = await fetch(url, { headers: { "Accept": "application/json" } })
      if (!res.ok) return this._hideResults()
      this._renderResults(await res.json())
    } catch (_) {
      this._hideResults()
    }
  }

  _renderResults(records) {
    const container = this.resultsTarget
    container.replaceChildren()

    if (records.length === 0) {
      const empty = document.createElement("div")
      empty.className = "px-3 py-2 text-sm text-gray-500"
      empty.textContent = "Brak kontrahentów pasujących do zapytania."
      container.appendChild(empty)
    } else {
      records.forEach(record => {
        const div = document.createElement("div")
        div.className = "px-3 py-2 cursor-pointer hover:bg-indigo-50 flex items-center justify-between gap-2"
        div.dataset.action = "click->vendor-picker#select"
        div.dataset.recordId = record.id
        div.dataset.recordName = record.name
        const span = document.createElement("span")
        span.className = "text-sm text-gray-900 truncate"
        span.textContent = record.name
        div.appendChild(span)
        container.appendChild(div)
      })
    }

    container.classList.remove("hidden")
  }

  _hideResults() {
    this.resultsTarget.replaceChildren()
    this.resultsTarget.classList.add("hidden")
  }

  _renderErrors(messages) {
    const ul = this.modalErrorsTarget
    ul.replaceChildren()
    if (messages.length === 0) {
      ul.classList.add("hidden")
      return
    }
    ul.classList.remove("hidden")
    messages.forEach(msg => {
      const li = document.createElement("li")
      li.className = "text-sm text-red-700"
      li.textContent = msg
      ul.appendChild(li)
    })
  }

  _onClickOutside(event) {
    if (!this.element.contains(event.target) && !this.modalTarget.contains(event.target)) {
      this._hideResults()
    }
  }
}
